import { Decoration, WidgetType } from "@codemirror/view";
import classNames from "classnames";
import _ from "lodash";
import React, { useState } from "react";
import ReactDOM from "react-dom/client";
import { hideLoanRegion, hideMoveRegion, linecolToPosition, makeDecorationField, ownChar, permName, readChar, showLoanRegion, showMoveRegion, writeChar } from "./misc";
let PermChar = ({ perm, facts }) => {
    // FIXME: don't reverse the abbreviated content.
    // ~~~~ IMPORTANT MAINTENANCE NOTE ~~~~
    // Any changes to this HTML must be propagated to permissions.rs in mdbook-aquascope!
    let getInner = () => {
        let kind = permName(perm.perm);
        let Perm = ({ children }) => (React.createElement("span", { className: classNames("perm", kind) }, children));
        if (perm.step.type === "None") {
            return perm.step.value ? (React.createElement("div", { className: "perm-diff-present", title: `Path had ${kind} permissions before the preceding line, and that didn't change after this line.` },
                React.createElement(Perm, null, perm.perm))) : (React.createElement("div", { className: "perm-diff-none", title: `Path did not have ${kind} permissions before the preceding line, and that didn't change after this line.` },
                React.createElement(Perm, null, "\u2012")));
        }
        else if (perm.step.type === "Low") {
            return (React.createElement("div", { className: "perm-diff-sub-container", title: `Path had ${kind} permissions before the preceding line, and lost it after this line.` },
                React.createElement("div", { className: "perm-diff-sub" }),
                React.createElement(Perm, null, perm.perm)));
        } /* perm.step.type === "High" */
        else {
            return (React.createElement("div", { title: `Path did not have ${kind} permissions before the preceding line, and gained it after this line.` },
                React.createElement("span", { className: "perm-diff-add" }, "+"),
                React.createElement(Perm, null, perm.perm)));
        }
    };
    return (React.createElement("td", { onMouseEnter: () => {
            showLoanRegion(facts, perm.loanKey, [permName(perm.perm)]);
            showMoveRegion(facts, perm.moveKey, [permName(perm.perm)]);
        }, onMouseLeave: () => {
            hideLoanRegion(facts, perm.loanKey, [permName(perm.perm)]);
            hideMoveRegion(facts, perm.moveKey, [permName(perm.perm)]);
        }, className: "perm-char" }, getInner()));
};
let PermDiffRow = ({ path, diffs, facts }) => {
    // There is a sort of hierarchy to the changing permissions:
    // We first prioritize "moves" and "borrows" (permission refinements),
    // these actions are visually important because they reflect concrete
    // actions in the source-code. Liveness is implicit, and should be
    // shown after the other actions.
    let visualFacts = [
        {
            fact: "path_moved",
            states: [
                {
                    value: { type: "High", value: 0 },
                    icon: "sign-out",
                    desc: "Path is moved here"
                },
                {
                    value: { type: "Low" },
                    icon: "recycle",
                    desc: "Path is re-initialized after move here"
                }
            ]
        },
        {
            fact: "loan_read_refined",
            states: [
                {
                    value: { type: "High", value: 0 },
                    icon: "arrow-right",
                    desc: "Path is borrowed here"
                },
                {
                    value: { type: "Low" },
                    icon: "rotate-left",
                    desc: "Borrow on path is dropped here"
                }
            ]
        },
        {
            fact: "loan_write_refined",
            states: [
                {
                    value: { type: "High", value: 0 },
                    icon: "arrow-right",
                    desc: "Path is borrowed here"
                },
                {
                    value: { type: "Low" },
                    icon: "rotate-left",
                    desc: "Borrow on path is no longer used here"
                }
            ]
        },
        {
            fact: "is_live",
            states: [
                {
                    value: { type: "High", value: 0 },
                    icon: "level-up",
                    desc: "Path is initialized here"
                },
                {
                    value: { type: "Low" },
                    icon: "level-down",
                    desc: "Path is no longer used here"
                }
            ]
        },
        {
            fact: "path_uninitialized",
            states: [
                {
                    value: { type: "High", value: 0 },
                    icon: "sign-out",
                    desc: "Path contains uninitialized data"
                },
                {
                    value: { type: "Low" },
                    icon: "recycle",
                    desc: "Path data is initialized after move here"
                }
            ]
        }
    ];
    let ico = null;
    loop: for (let { fact, states } of visualFacts) {
        for (let { value, icon, desc } of states) {
            if (_.isEqual(diffs[fact].type, value.type)) {
                ico = (React.createElement("i", { className: `fa fa-${icon} aquascope-action-indicator`, title: desc }));
                break loop;
            }
            else {
                // console.log("unequal: ", diffs[fact].type, value.type);
            }
        }
    }
    let unwrap = (v) => v.type === "None" || v.type === "High" ? v.value : undefined;
    let moveKey = unwrap(diffs.path_moved);
    let perms = [
        {
            perm: readChar,
            step: diffs.permissions.read,
            loanKey: unwrap(diffs.loan_read_refined),
            moveKey
        },
        {
            perm: writeChar,
            step: diffs.permissions.write,
            loanKey: unwrap(diffs.loan_write_refined),
            moveKey
        },
        {
            perm: ownChar,
            step: diffs.permissions.drop,
            loanKey: unwrap(diffs.loan_drop_refined),
            moveKey
        }
    ];
    let pathCol = React.createElement("td", { className: "perm-step-path" }, path);
    return (React.createElement("tr", null,
        pathCol,
        React.createElement("td", { className: "perm-step-event" }, ico),
        perms.map(perm => (React.createElement(PermChar, { key: perm.perm, perm: perm, facts: facts })))));
};
let stepLocation = (step) => {
    return step.location.end;
};
let StepTable = ({ rows, facts }) => (React.createElement("table", { className: "perm-step-table" },
    React.createElement("tbody", null, rows.map(([path, diffs], i) => (React.createElement(PermDiffRow, { key: i, path: path, diffs: diffs, facts: facts }))))));
// An individual table goes inside of the overall container, and
// contains its own dropdown based on focused / unfocused places.
let StepTableIndividual = ({ focused, hidden, facts }) => {
    let [displayAll, setDisplayAll] = useState(false);
    let hiddenDropdown = hidden.length > 0 ? (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "step-table-dropdown step-widget-toggle" }, "\u25CF \u25CF \u25CF"),
        React.createElement("div", { className: classNames({ "hidden-height": !displayAll }) },
            React.createElement(StepTable, { rows: hidden, facts: facts })))) : null;
    return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: TODO
    React.createElement("div", { className: classNames("step-table-container", {
            "contains-hidden": hidden.length > 0
        }), onClick: () => setDisplayAll(!displayAll) },
        React.createElement(StepTable, { rows: focused, facts: facts }),
        hiddenDropdown));
};
// On a single line there can be multiple tables next to each other.
// All tables go in a container that can be collapsed to the left.
let StepLine = ({ spaces, focusedRegex, tables, init, facts }) => {
    let [display, setDisplay] = useState(init);
    let arrowOut = "»";
    let arrowIn = "«";
    let together = tables.map((table, i) => {
        let [focusedDiffs, hiddenDiffs] = _.partition(table.state, ([path, _]) => !!path.match(focusedRegex));
        return (React.createElement(StepTableIndividual, { key: i, focused: focusedDiffs, hidden: hiddenDiffs, facts: facts }));
    });
    return (React.createElement("div", { className: "perm-step-widget" },
        React.createElement("span", { className: "step-widget-toggle", onClick: () => setDisplay(!display) }, display ? arrowIn : arrowOut),
        React.createElement("div", { className: classNames("step-widget-container", {
                "hidden-width": !display
            }) },
            React.createElement("div", { className: "step-widget-spacer" }, spaces),
            together)));
};
class PermissionStepLineWidget extends WidgetType {
    view;
    step;
    facts;
    annotations;
    line;
    rowHTML;
    constructor(view, step, facts, annotations) {
        super();
        this.view = view;
        this.step = step;
        this.facts = facts;
        this.annotations = annotations;
        this.line = view.state.doc.lineAt(linecolToPosition(stepLocation(step), view.state.doc));
    }
    eq(other) {
        // Only one table widget can (should) be on a line.
        return this.line.number === other.line.number;
    }
    toDOM() {
        let container = document.createElement("span");
        let doc = this.view.state.doc;
        let currLine = this.line;
        let initDisplay = this.annotations && this.annotations.focused_lines.length > 0
            ? this.annotations.focused_lines.includes(currLine.number)
            : true;
        let maxLineLen = 0;
        for (let line of doc.iterLines()) {
            maxLineLen = Math.max(maxLineLen, line.length);
        }
        let padding = 2 + maxLineLen - currLine.length;
        let spaces = "―".repeat(padding);
        let matchers = this.annotations?.focused_paths[currLine.number];
        let rx = matchers
            ?.map(matcher => matcher.type === "Literal"
            ? _.escapeRegExp(matcher.value)
            : matcher.value)
            .map(s => `(${s})`)
            .join("|");
        let r = new RegExp(rx ?? "(.*)?");
        let tables = this.step.state;
        ReactDOM.createRoot(container).render(React.createElement(StepLine, { spaces: spaces, focusedRegex: r, tables: tables, init: initDisplay, facts: this.facts }));
        return container;
    }
    ignoreEvent() {
        return true;
    }
}
export let stepField = makeDecorationField();
export function makeStepDecorations(view, facts, stateSteps, annotations) {
    return stateSteps.map(step => Decoration.widget({
        widget: new PermissionStepLineWidget(view, step, facts, annotations),
        side: 1
    }).range(linecolToPosition(stepLocation(step), view.state.doc)));
}
//# sourceMappingURL=stepper.js.map